import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Text, Box, PageBlock } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';

import { EditResume } from 'src/components/Profile/Resume/EditResume/EditResume';
import { START_RESUME } from 'src/components/Router';
import { CardRoute } from 'src/components/Router/CardRoute';
import { usePage } from 'src/components/Router/usePage.ts';
import { analyticsClient } from 'src/modules/analytics/index.ts';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';

import { usePersonalDetails } from '../PersonalDetails/data.ts';
import { useResumes } from '../Resume/data';

import translations from './.vocab';
import { CreateResumeBlueCard } from './CreateResumeBlueCard/CreateResumeBlueCard';
import { Page } from './Page/Page';
import { ResumeLimit } from './ResumeLimit/ResumeLimit';
import { StickyHeader } from './StickyHeader/StickyHeader';
import { createResumeRendered } from './actionCreators';
import { CreateStep } from './reducer';
import { useGetCreateResumeRoutes } from './routes';
import { StartResume } from './svg/StartResume';

const rowHeight = 6;

export const CreateResume = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { brand } = useAppConfig();
  const { loading: personalDetailsLoading } = usePersonalDetails();
  const createStep = useTypedSelector((store) => store.createResume.createStep);
  const { resumes } = useResumes();
  const resumeCount = resumes.length;
  const profileUrl = useMelwaysLink({ path: '/profile', absolute: true });
  const [pageMarginBottom, setPageMarginBottom] = useState<number | string>(
    '60vh',
  );
  const currentPage = usePage();

  const setPageMargin = () => {
    const exactHeight = 0.6 * window.innerHeight;
    const remainder = exactHeight % rowHeight;
    const roundedHeight =
      remainder <= rowHeight / 2
        ? exactHeight - remainder
        : exactHeight + (rowHeight - remainder);
    setPageMarginBottom(roundedHeight);
  };

  useEffect(() => {
    if (!personalDetailsLoading) {
      dispatch(createResumeRendered());
      analyticsClient.pageUpdated({ currentPage });
    }

    setPageMargin();
  }, [dispatch, personalDetailsLoading, currentPage]);

  const createResumeRoutes = useGetCreateResumeRoutes();

  const renderCreateStep = () => {
    if (createStep === CreateStep.RESUME_LIMIT_REACHED) {
      return <ResumeLimit />;
    } else if (createStep === CreateStep.RESUME_CREATED) {
      return <EditResume />;
    } else if (createStep === CreateStep.PROFILE_REVIEW) {
      return (
        <>
          <Page
            name={START_RESUME}
            marginBottom={pageMarginBottom}
            resumeCount={resumeCount}
          >
            <CardRoute threshold={0.25}>
              <CreateResumeBlueCard
                heading={t('Update your profile')}
                svg={<StartResume />}
              >
                <Text size="large" component="p">
                  {t('Create a resumé that helps you stand out to employers.')}
                </Text>
              </CreateResumeBlueCard>
            </CardRoute>
          </Page>
          {createResumeRoutes.map((card) => (
            <Page
              name={card}
              key={card}
              marginBottom={pageMarginBottom}
              resumeCount={resumeCount}
            >
              <CardRoute card={card} threshold={0.25} />
            </Page>
          ))}
        </>
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('Create a resumé from your brand profile', { brand })}</title>
        <link rel="canonical" href={profileUrl} />
      </Helmet>
      <StickyHeader />
      <PageBlock width="medium">
        <Box maxWidth="small">{renderCreateStep()}</Box>
      </PageBlock>
    </>
  );
};
